import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { scrollBelowStickyBars } from '@/common/scrollBelowStickyBars';
import styled from '@emotion/styled';
import { useForm, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useMediaQuery } from '@mui/material';
import { userAtom } from '@/state/loginState';
import Text from '@/components/common/elements/Text';
import Container from '@/components/common/elements/Container';
import Button from '@/components/common/elements/Button';
import { KakaoAddressDocument } from '@/common/types';
import { api } from '@/api/repository/axios';
import CustomModal from '@/components/common/elements/Modal';
import ResultModal from '@/components/common/elements/ResultModal';
import LoadingSpinner from '@/components/common/elements/LoadingSpinner';
import { useIsMobile } from '@/common/hooks';
import { focusField, focusFieldAfterModal } from '@/common/focusField';

import { designSystem } from '@/styles/theme';
import { FormSaveBar } from '@/components/common/FormNavigation';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import { GuideLines, SectionTitle } from '@/pages/Seminar/components/form/formStyles';
import OnlineAccessFields from '@/pages/Seminar/components/form/OnlineAccessFields';
import InstructorCouponSection, { CouponPendingUpdate, firstDraftProblem, pendingUpdateProblem } from '@/pages/Seminar/components/form/InstructorCouponSection';
import { errorMessageOf } from '@/components/domain/coupon/couponFormat';
import type { CouponDraft } from '@/pages/Seminar/components/form/InstructorCouponDialog';
import type { SeminarCouponCreateResult } from '@/api/repository/types/types';
import {
  GENERAL_DEFAULT_CAPACITY,
  ONLINE_GUIDE_MAX_LENGTH,
  SeminarFormatValue,
  SeminarScheduleValue,
  SeminarTypeValue,
  SessionInput,
  buildScheduleLabel,
  formatDateOnly,
  isValidOnlineUrl,
  normalizeOnlineUrl,
  OPEN_CANCEL_NOTICE,
  midwayCancelNoticeOf,
  canCancelSeminar,
  isSeminarInProgress,
} from '@/pages/Seminar/components/form/seminarFormUtils';
import {
  CouponBasis,
  DraftStation,
  FIELD_ORDER,
  FIELD_STEP,
  MetaGroup,
  STEP_META,
  SeminarDraft,
  SeminarFormStep,
  SeminarFormValues,
  buildPayload,
  clearDraft,
  collectErrors,
  couponBasisOf,
  effectiveSchedule,
  fieldFromServerMessage,
  fieldsOfStep,
  loadDraft,
  needsAddress,
  sameCouponBasis,
  saveDraft,
  schema,
  toFormDateTime,
} from '@/pages/Seminar/components/form/seminarFormModel';
import {
  SeminarFormContext,
  SeminarFormContextValue,
  SeminarManageStatus,
  StepProblem,
} from '@/pages/Seminar/components/form/steps/SeminarFormContext';
import {
  FormPage,
  FormTopBar,
  GhostButton,
  PHONE_QUERY,
  PcFormHeader,
  PcGrid,
  PcSide,
  PhoneActionBar,
  PrimaryButton,
  ProgressStrip,
  ResumeDraftBanner,
  StepCard,
  useHeaderHeight,
} from '@/pages/Seminar/components/form/steps/SeminarFormLayout';
import StepBasic from '@/pages/Seminar/components/form/steps/StepBasic';
import CareerGateSheet, { hideCareerGateToday, isCareerGateHiddenToday } from '@/pages/Seminar/components/form/CareerGateSheet';
import StepSchedulePlace from '@/pages/Seminar/components/form/steps/StepSchedulePlace';
import StepRecruitPrice from '@/pages/Seminar/components/form/steps/StepRecruitPrice';
import StepIntro from '@/pages/Seminar/components/form/steps/StepIntro';
import StepConfirm, { SeminarFormPreview, StepStatusList } from '@/pages/Seminar/components/form/steps/StepConfirm';
import { preventEnterSubmit } from '@/components/common/form/preventEnterSubmit';

// ─── 로딩·접속 정보만 수정 화면(예전 틀 유지) ──────────────────────────────

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background-color: #fff;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: 2px solid #f0f0f0;
  padding: 32px;
  background: linear-gradient(to bottom, #fafafa, #fff);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const FormContent = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`
  padding: 12px 24px;
  background-color: ${props => props.disabled ? '#e2e8f0' : designSystem.colors.primary};
  color: ${props => props.disabled ? '#94a3b8' : '#ffffff'};
  border: none;
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 15px;
  font-weight: 600;

  @media (max-width: 768px) {
    flex: 1;
    padding: 10px 20px;
    font-size: 14px;
  }
`;

/** 새 등록 빈 값 */
const EMPTY_VALUES: SeminarFormValues = {
  seminarType: 'GENERAL',
  title: '',
  description: '',
  instructor: '',
  categories: [],
  level: '',
  format: 'OFFLINE',
  schedule: 'SINGLE',
  startDateTime: '',
  endDateTime: '',
  periodStartDate: '',
  periodEndDate: '',
  sessions: [],
  originalStartDateTime: '',
  originalEndDateTime: '',
  originalTitle: '',
  contact: '',
  recruitmentStartDateTime: '',
  recruitmentEndDateTime: '',
  location: '',
  address: '',
  addressCode: '',
  latitude: undefined,
  longitude: undefined,
  stations: [],
  onlineUrl: '',
  onlineGuide: '',
  isOnline: false,
  price: undefined as unknown as number,
  capacity: undefined as unknown as number,
  thumbnailUrl: '',
};

/** 사람이 입력한 게 있는지(임시 저장·나가기 확인 기준) — 자동으로 채워지는 강사명·기본 선택은 빼고 본다 */
const hasUserInput = (values: Partial<SeminarFormValues>, couponDrafts: CouponDraft[]): boolean =>
  !!(values.title?.trim()
    || (values.description && values.description.replace(/<[^>]*>/g, '').trim())
    || (values.description && values.description.includes('<img'))
    || (values.categories && values.categories.length > 0)
    || values.level
    || values.startDateTime || values.endDateTime || values.periodStartDate || values.periodEndDate
    || (values.sessions && values.sessions.length > 0)
    || values.address || values.location?.trim() || values.onlineUrl?.trim() || values.onlineGuide?.trim()
    || (values.price != null && String(values.price) !== '')
    || (values.capacity != null && String(values.capacity) !== '')
    || values.recruitmentStartDateTime || values.recruitmentEndDateTime
    || values.thumbnailUrl
    || couponDrafts.length > 0);

/** 단계 설명(제목 아래 한 줄) */
const stepSubOf = (step: SeminarFormStep, career: boolean, paymentEnabled: boolean, isPhone: boolean): string => {
  switch (step) {
    case 1: return paymentEnabled ? '유형에 따라 신청·결제를 받는 곳이 달라져요.' : '제목을 정해 주세요.';
    case 2: return career ? '오프라인은 장소를, 라이브·동영상은 접속 링크를 받아요.' : '일정과 장소를 알려 주세요.';
    case 3: return career ? '참가비를 넣으면 정산 예상 금액을 바로 보여 드려요.' : '참가비는 목록과 상세에 보여 주기만 해요.';
    case 4: return '썸네일과 설명은 목록과 상세에 그대로 보여요.';
    default: return isPhone ? '빠진 항목을 누르면 그 칸으로 이동해요.' : '오른쪽 단계 목록에서 빠진 항목을 누르면 그 칸으로 이동해요.';
  }
};

const LAST_STEP: SeminarFormStep = 5;
/** 임시 저장 간격 — 입력이 멈춘 뒤 저장 */
const AUTOSAVE_DELAY_MS: number = 600;

const SeminarForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const user = useRecoilValue(userAtom);
  const isMobile = useIsMobile();
  /** 폰 5단계 / PC 3칸 기준 (계약: 768px 이하 폰) */
  const isPhone: boolean = useMediaQuery(PHONE_QUERY);
  const headerHeight: number = useHeaderHeight();

  // 새 등록: 저장해 둔 초안이 있으면 [이어서 쓰기][새로 쓰기]를 먼저 묻는다(고르기 전에는 덮어쓰지 않는다)
  const [pendingDraft, setPendingDraft] = useState<SeminarDraft | null>(() => {
    if (isEditMode) return null;
    const found: SeminarDraft | null = loadDraft();
    return found && hasUserInput(found.formValues ?? {}, found.couponDrafts ?? []) ? found : null;
  });
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);
  /** 등록 성공 뒤에는 임시 저장을 다시 만들지 않는다 */
  const submittedRef = useRef<boolean>(false);

  const [step, setStep] = useState<SeminarFormStep>(1);
  /** 새 등록에서 지나온 가장 먼 단계 — 진행 막대로는 여기까지 갈 수 있다 */
  const [maxStep, setMaxStep] = useState<SeminarFormStep>(1);
  const [editorKey, setEditorKey] = useState<number>(0);
  const [exitConfirmOpen, setExitConfirmOpen] = useState<boolean>(false);
  const pageRef = useRef<HTMLDivElement | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(isEditMode);
  // 고른 지하철역(이름 포함) — 폼 값 stations 는 id 배열(제출 형식과 같음)
  const [stationObjects, setStationObjects] = useState<DraftStation[]>([]);
  const [showAlertModal, setShowAlertModal] = useState(false);
  // 신규 등록 전 안내 확인 모달 (검증 통과한 데이터를 보관)
  const [pendingSubmit, setPendingSubmit] = useState<SeminarFormValues | null>(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertCallback, setAlertCallback] = useState<(() => void) | null>(null);
  // 알림 모달의 보조 액션 버튼 (예: '폐강하러 가기')
  const [alertAction, setAlertAction] = useState<{ label: string; onClick: () => void } | null>(null);
  // 수정 모드에서 세미나 관리(마감/재모집/폐강)용 현재 상태
  const [seminarStatus, setSeminarStatus] = useState<SeminarManageStatus | null>(null);
  // 수정 화면: 저장된 쿠폰 기준(참가비·모집 마감). 서버는 쿠폰을 저장된 값으로 검사하므로, 화면 값이 이와 다르면
  // 쿠폰 만들기·고치기를 바로 보내지 않고 담아 뒀다가 세미나를 저장한 뒤 반영한다(강사가 모르는 저장값 기준 오류가 뜨던 문제)
  const [savedCouponBasis, setSavedCouponBasis] = useState<CouponBasis | null>(null);
  // 수정 화면: 저장 전 담아 둔 기존 쿠폰 고치기(새 쿠폰은 couponDrafts) · 쿠폰 목록 다시 읽기 키
  const [couponUpdates, setCouponUpdates] = useState<CouponPendingUpdate[]>([]);
  const [couponReloadKey, setCouponReloadKey] = useState<number>(0);
  const [managing, setManaging] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // 비소유자가 수정 URL로 직접 진입 시: 폼을 렌더하지 않고 접근 권한 없음 모달만 노출
  const [accessDeniedMessage, setAccessDeniedMessage] = useState<string | null>(null);
  // 접근 차단 모달의 버튼을 '참여인원 관리'로 보낼지 (진행 중이라 수정은 안 되지만 폐강은 되는 세미나)
  const [deniedGoParticipants, setDeniedGoParticipants] = useState(false);
  const [categoryGroups, setCategoryGroups] = useState<MetaGroup[]>([]);
  // 세미나 유형 (GENERAL=일반 / CAREER=명치사 커리어). 결제 기능 OFF면 항상 GENERAL.
  // 새 등록 기본은 명치사 커리어(사용자 결정 2026-09-29). 정산계좌·약관이 없는 강사는 프로필을 받은 뒤 일반으로 돌린다
  const [seminarType, setSeminarType] = useState<SeminarTypeValue>(isEditMode ? 'GENERAL' : 'CAREER');
  const [profileChecked, setProfileChecked] = useState<boolean>(false);
  // 수정 시: 원본 유형 + 참여자 존재 여부 (참여자 있는 커리어→일반 변경 차단용)
  const [originalSeminarType, setOriginalSeminarType] = useState<SeminarTypeValue>('GENERAL');
  // 명치사 커리어 외부 결제 유도 금지 동의(강사 약관 제4조의3) — 커리어로 새로 등록·전환할 때 필수. 이미 커리어인 세미나 수정은 다시 묻지 않는다
  const [directDealAgreed, setDirectDealAgreed] = useState<boolean>(false);
  const [hasActiveParticipants, setHasActiveParticipants] = useState(false);
  // 세미나 결제(커리어) 기능 플래그 — 유형 선택 UI 노출 여부 결정
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  // 강사 프로필 (커리어 선택 시 정산계좌·약관동의 완결성 검사에 사용)
  const [instructorProfile, setInstructorProfile] = useState<any>(null);
  // 시작된 커리어 라이브·동영상: 접속 링크·안내만 수정하는 화면
  const [accessOnlyMode, setAccessOnlyMode] = useState(false);
  // 신규 등록에서 함께 만들 쿠폰 초안(커리어 유료) — 등록 요청 coupons[] 로 보낸다
  const [couponDrafts, setCouponDrafts] = useState<CouponDraft[]>([]);
  // 참가비 0·일반 유형으로 바뀌어 초안을 비웠을 때 안내 한 줄
  const [couponDraftNotice, setCouponDraftNotice] = useState('');
  // 상시 동영상 판매 종료·재개 처리 중
  const [togglingSales, setTogglingSales] = useState(false);
  // 명치사 커리어 등록 안내(정산계좌·약관 미비) — 새 등록에 들어올 때 한 번 자동으로, 커리어 카드를 누르면 언제든
  const [careerGateOpen, setCareerGateOpen] = useState<boolean>(false);
  /** 이번 화면에서 자동 안내를 이미 판단했는지(띄웠거나 안 띄우기로 했거나) */
  const careerGateAutoDoneRef = useRef<boolean>(false);

  /** 화면·전송에 실제로 쓰는 유형 — 결제 기능이 꺼져 있으면 유형 선택 UI 가 없으므로 항상 일반 */
  const effectiveType: SeminarTypeValue = paymentEnabled ? seminarType : 'GENERAL';
  const career: boolean = effectiveType === 'CAREER';

  useEffect(() => {
    api.category.list().then((res: any) => setCategoryGroups(res.result ?? [])).catch(console.error);
  }, []);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    formState: { errors, isDirty },
  } = useForm<SeminarFormValues>({
    resolver: yupResolver(schema) as unknown as Resolver<SeminarFormValues>,
    mode: 'onChange',
    defaultValues: EMPTY_VALUES,
  });

  // 옛 그룹 코드(CLINICAL/MANAGEMENT 등)가 draft/수정 데이터에 섞여 있으면 자동 제거.
  // 사용자에게 보이지 않는 chip은 해제할 방법이 없어 submit 시 백엔드가 거부하기 때문.
  const currentCategories: string[] = watch('categories') || [];
  useEffect(() => {
    if (categoryGroups.length === 0) return;
    const validCodes = new Set(categoryGroups.flatMap(g => g.categories.map(c => c.code)));
    const filtered = currentCategories.filter(c => validCodes.has(c));
    if (filtered.length !== currentCategories.length) {
      setValue('categories', filtered, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryGroups, currentCategories.join(',')]);

  const watchValues = watch();
  // 쿠폰 창·검사·미리보기·저장 전 판정이 모두 쓰는 기준 — 폼에 지금 입력된 참가비·모집 마감
  const couponBasis: CouponBasis = couponBasisOf({ ...watchValues, seminarType: effectiveType });

  // 참가비를 0원으로 바꾸거나 일반 유형으로 바꾸면 쿠폰 초안은 쓸 수 없다 — 비우고 한 줄 안내(빈 칸으로 지우는 중에는 두기)
  const priceValue: unknown = watchValues.price;
  const priceIsZero: boolean = priceValue !== undefined && priceValue !== null && String(priceValue) !== '' && Number(priceValue) === 0;
  const careerForDrafts: boolean = (paymentEnabled ? seminarType : 'GENERAL') === 'CAREER';
  useEffect(() => {
    if (couponDrafts.length === 0) return;
    if (careerForDrafts && !priceIsZero) return;
    // 이어 쓰기 직후 결제 기능 플래그를 아직 못 받았을 때 지우지 않게 — 플래그를 받은 뒤에만 판단
    if (!paymentEnabled) return;
    setCouponDrafts([]);
    setCouponDraftNotice(careerForDrafts
      ? '참가비가 0원이라 만들어 둔 쿠폰을 지웠어요.'
      : '일반 세미나는 쿠폰을 쓸 수 없어 만들어 둔 쿠폰을 지웠어요.');
  }, [careerForDrafts, priceIsZero, couponDrafts.length, paymentEnabled]);

  // 정산계좌 = 은행명·계좌번호·예금주 셋 다 있어야 한다(하나라도 비면 커리어 안내 팝업) — 공백만 있어도 빈 값
  const hasSettlementAccount: boolean = [instructorProfile?.settlementBank, instructorProfile?.settlementAccount, instructorProfile?.settlementHolder]
    .every((v: unknown) => String(v ?? '').trim() !== '');
  const hasInstructorTerms: boolean = instructorProfile?.termsAgreed === true;
  const careerEligible: boolean = hasSettlementAccount && hasInstructorTerms;
  /** "오늘은 그만 보기" 저장 키 — 회원별 */
  const memberKey: string = String(user?.no ?? user?.id ?? user?.email ?? 'guest');

  // 새 등록 기본 커리어 — 커리어를 등록할 수 없는 강사(정산계좌·약관 미비)면 일반으로 시작한다.
  // 프로필을 받기 전에 이어 쓴 커리어 초안도 여기서 일반으로 돌린다(카드로 커리어를 고르는 길은 handleSeminarTypeChange 가 막는다)
  useEffect(() => {
    if (isEditMode || !profileChecked || seminarType !== 'CAREER') return;
    if (!careerEligible) setSeminarType('GENERAL');
  }, [isEditMode, profileChecked, careerEligible, seminarType]);

  // 새 등록에 들어올 때마다 — 결제 기능이 켜져 있고 정산계좌·약관이 비었으면 커리어 등록 안내를 띄운다.
  // 이어 쓰기 배너가 떠 있으면 고른 뒤 판단한다([새로 쓰기]면 띄우고, [이어서 쓰기]면 띄우지 않는다). 오늘 그만 보기면 자동으로는 안 띄운다
  useEffect(() => {
    // 회원 정보(오늘 그만 보기 키)를 받은 뒤 판단한다
    if (isEditMode || careerGateAutoDoneRef.current || !user || !profileChecked || !paymentEnabled || pendingDraft) return;
    careerGateAutoDoneRef.current = true;
    if (careerEligible || isCareerGateHiddenToday(memberKey)) return;
    setCareerGateOpen(true);
  }, [isEditMode, user, profileChecked, paymentEnabled, pendingDraft, careerEligible, memberKey]);

  const closeCareerGate = (hideToday: boolean): void => {
    if (hideToday) hideCareerGateToday(memberKey);
    setCareerGateOpen(false);
    setSeminarType('GENERAL');
  };

  const goInstructorEdit = (hideToday: boolean): void => {
    closeCareerGate(hideToday);
    // 저장하면 이 화면으로 돌아온다(프로필이 채워졌으면 커리어로 시작)
    // 강사 화면은 비어 있는 곳(정산계좌 → 없으면 약관)으로 바로 스크롤·포커스한다
    navigate('/seminar/instructor/edit', {
      state: { returnTo: `${location.pathname}${location.search}`, focus: hasSettlementAccount ? 'terms' : 'settlement' },
    });
  };

  // 화면 유형을 폼 값에도 넣어 검증 분기가 같은 기준을 쓰게 한다
  useEffect(() => {
    setValue('seminarType', effectiveType);
  }, [effectiveType, setValue]);

  // 강사 테이블에서 이름 조회 + 프로필 보관(커리어 선택 게이트용)
  // 일반 세미나 등록은 아무 확인 없이 바로 가능하며, 정산계좌·약관 검사는 커리어 선택 시점에만 수행한다.
  useEffect(() => {
    const fetchInstructorProfile = async () => {
      try {
        const response = await api.instructor.getMyProfile();
        const profile = response.code === 0 ? response.result : null;

        if (profile) setInstructorProfile(profile);

        if (!isEditMode) {
          setValue('instructor', profile?.name || user?.name || '');
        }
      } catch {
        if (!isEditMode) setValue('instructor', user?.name || '');
      } finally {
        setProfileChecked(true);
      }
    };
    fetchInstructorProfile();

    // 세미나 결제(커리어) 기능 플래그 조회 — 유형 선택 UI 및 비교표 노출 여부 결정
    const fetchPaymentFlag = async () => {
      try {
        const flags = await api.point.getFeatureFlags();
        setPaymentEnabled(flags?.result?.seminarPayment?.enabled === true);
      } catch {
        setPaymentEnabled(false);
      }
    };
    fetchPaymentFlag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, user]);

  const showAlert = useCallback((message: string, callback?: () => void, action?: { label: string; onClick: () => void }) => {
    setAlertMessage(message);
    setShowAlertModal(true);
    setAlertCallback(() => callback || null);
    setAlertAction(action || null);
  }, []);

  const closeAlert = () => {
    setShowAlertModal(false);
    if (alertCallback) {
      alertCallback();
      setAlertCallback(null);
    }
  };

  // 세미나 유형 변경 — 커리어 선택 시 정산계좌(은행+계좌) + 약관동의 완결성 검사.
  // 미비 시 커리어 선택을 막고 일반으로 되돌린 뒤 강사 정보수정 페이지로 유도한다.
  const handleSeminarTypeChange = (type: SeminarTypeValue) => {
    // 참여자가 있는 커리어 세미나는 일반으로 변경 불가 (백엔드에서도 저장 시 차단)
    if (type === 'GENERAL' && isEditMode && originalSeminarType === 'CAREER' && hasActiveParticipants) {
      showAlert('참여자가 있는 커리어 세미나는 일반 세미나로 바꿀 수 없어요.\n바꾸려면 폐강(전원 환불) 후 새로 등록해 주세요.');
      return;
    }
    if (type === 'CAREER' && !careerEligible) {
      // 오늘 그만 보기와 상관없이 같은 안내를 띄운다 — 닫으면 일반 그대로
      setSeminarType('GENERAL');
      setCareerGateOpen(true);
      return;
    }
    setSeminarType(type);
  };

  // 진행 방식 변경 — 동영상은 일정이 '기간(시청 기간)' 또는 '상시'. 동영상이 아니면 상시를 기간으로 되돌린다
  const handleFormatChange = (format: SeminarFormatValue) => {
    setValue('format', format, { shouldValidate: false });
    const current: SeminarScheduleValue = watch('schedule');
    if (format === 'VOD' && current !== 'OPEN') setValue('schedule', 'PERIOD');
    if (format !== 'VOD' && current === 'OPEN') {
      // 상시 판매 시작일(기본 오늘)은 기간형 시작일 규칙(내일 이후)과 맞지 않는다 — 비우고 다시 고르게
      setValue('schedule', 'PERIOD');
      setValue('periodStartDate', '');
      setValue('periodEndDate', '');
    }
  };

  const today: string = formatDateOnly(new Date());

  // 일정 유형 변경 — 동영상 상시는 판매 시작일 기본값 = 오늘(등록하면 바로 공개)
  const handleScheduleChange = (value: SeminarScheduleValue) => {
    if (watch('format') === 'VOD') {
      setValue('schedule', value, { shouldValidate: false });
      if (value === 'OPEN' && !watch('periodStartDate')) setValue('periodStartDate', today);
      return;
    }
    setValue('schedule', value);
  };

  // 수정 모드일 때 기존 세미나 데이터 로드
  useEffect(() => {
    if (!isEditMode || !id) return;

    const fetchSeminarData = async () => {
      try {
        const response = await api.seminar.getDetail(id);
        if (response.code === 0) {
          const data = response.result;

          // 소유자(작성 강사)가 아니면 수정 페이지 접근 차단.
          // 백엔드도 update/delete를 isOwner로 막지만, 화면 자체를 렌더하지 않고 권한 없음 모달만 노출한다.
          if (data.isAuthor === false) {
            setIsLoadingData(false);
            setAccessDeniedMessage('세미나 수정 권한이 없습니다.');
            return;
          }

          // 시작된 커리어 세미나는 전체 수정 불가 — 백엔드 started-guard 및 상세의 수정버튼 비활성화와 일치.
          // 단, 라이브·동영상은 종료 전까지 접속 링크·안내만 바꿀 수 있다(PATCH online-access).
          // 일반은 안내용이라 진행 중에도 전체 수정할 수 있다(서버도 허용).
          const nowMs = Date.now();
          const hasStarted = data.startDateTime ? new Date(data.startDateTime).getTime() <= nowMs : false;
          const hasEnded = data.endDateTime ? new Date(data.endDateTime).getTime() <= nowMs : false;
          // 상시 동영상은 판매 시작일이 지나도 전체 수정(서버 허용) — 결제자가 있으면 참가비·일정만 잠근다
          const openSaleDoc: boolean = data.format === 'VOD' && data.schedule === 'OPEN' && !data.isCancelled;
          if (hasStarted && data.seminarType === 'CAREER' && !openSaleDoc) {
            const onlineFormat = data.format === 'LIVE' || data.format === 'VOD';
            if (!onlineFormat || hasEnded || data.isCancelled) {
              setIsLoadingData(false);
              // 진행 중인 기간·회차 세미나는 폐강할 수 있다 — 폐강 버튼이 있는 참여인원 관리로 안내
              if (canCancelSeminar(data)) {
                setDeniedGoParticipants(true);
                setAccessDeniedMessage('이미 시작된 세미나는 수정할 수 없어요.\n폐강은 참여인원 관리 화면에서 할 수 있어요.');
              } else {
                setAccessDeniedMessage('이미 시작된 세미나는 수정할 수 없습니다.');
              }
              return;
            }
            setAccessOnlyMode(true);
          }

          const location = typeof data.location === 'object' ? data.location : null;

          // 백엔드 nearbyStations({id, line, stationName})를 칩 형식({id, name, lineNo})으로 변환
          const stationsFromResponse = Array.isArray(location?.nearbyStations) ? location.nearbyStations : [];
          const mappedNearbyStations: DraftStation[] = stationsFromResponse
            .filter((s: any) => s && s.id)
            .map((s: any) => ({
              id: s.id,
              name: s.stationName,
              lineNo: s.line,
            }));

          // 새 필드가 없는 옛 문서는 오프라인·당일로 읽는다 (서버 폴백과 같은 규칙)
          const format: SeminarFormatValue =
            data.format === 'LIVE' || data.format === 'VOD' ? data.format : 'OFFLINE';
          const schedule: SeminarScheduleValue = format === 'VOD'
            ? (data.schedule === 'OPEN' ? 'OPEN' : 'PERIOD')
            : data.schedule === 'PERIOD' || data.schedule === 'SESSIONS' ? data.schedule : 'SINGLE';
          const sessions: SessionInput[] = Array.isArray(data.sessions)
            ? data.sessions
              .filter((s: any) => s?.startDateTime && s?.endDateTime)
              .map((s: any) => ({
                date: String(s.startDateTime).slice(0, 10),
                startTime: String(s.startDateTime).slice(11, 16),
                endTime: String(s.endDateTime).slice(11, 16),
              }))
            : [];
          // 접속 정보는 공개 응답에서 빠지고 강사 본인에게만 onlineAccess 로 온다
          const onlineAccess = data.onlineAccess ?? null;

          reset({
            seminarType: data.seminarType === 'CAREER' ? 'CAREER' : 'GENERAL',
            title: data.title || '',
            description: data.description || '',
            instructor: data.instructorNickname || data.instructor || data.organizer || '',
            categories: data.categories?.map((c: any) => c.code) || [],
            level: typeof data.level === 'object' ? data.level.code : (data.level || ''),
            format,
            schedule,
            startDateTime: toFormDateTime(data.startDateTime),
            // 상시 동영상 endDateTime 은 자리값(+10년) — 폼 어디에도 싣지 않는다
            endDateTime: schedule === 'OPEN' ? '' : toFormDateTime(data.endDateTime),
            periodStartDate: data.startDateTime ? String(data.startDateTime).slice(0, 10) : '',
            // 상시는 종료일이 자리값(+10년)이라 폼에 싣지 않는다
            periodEndDate: data.endDateTime && schedule !== 'OPEN' ? String(data.endDateTime).slice(0, 10) : '',
            sessions,
            originalStartDateTime: toFormDateTime(data.startDateTime),
            originalEndDateTime: schedule === 'OPEN' ? '' : toFormDateTime(data.endDateTime),
            originalTitle: data.title || '',
            contact: data.contact || '',
            recruitmentStartDateTime: toFormDateTime(data.recruitmentStartDateTime),
            recruitmentEndDateTime: toFormDateTime(data.recruitmentEndDateTime),
            location: location?.detailAddress || (typeof data.location === 'string' ? data.location : '') || data.addressDetail || '',
            address: location?.address || data.address || location?.fullAddress || '',
            addressCode: location?.addressCode || data.addressCode || '',
            latitude: location?.coordinates?.latitude ? String(location.coordinates.latitude) : (data.latitude ? String(data.latitude) : undefined),
            longitude: location?.coordinates?.longitude ? String(location.coordinates.longitude) : (data.longitude ? String(data.longitude) : undefined),
            // form value(stations)는 station id 배열만 저장 (제출 payload 형식과 동일)
            stations: mappedNearbyStations.map((s: { id: string }) => s.id),
            onlineUrl: onlineAccess?.url || data.onlineUrl || '',
            onlineGuide: onlineAccess?.guide || data.onlineGuide || '',
            isOnline: data.isOnline === true,
            price: data.participationFee ?? data.price ?? 0,
            // 정원 없는 상시 동영상은 서버가 maxParticipants 10000·remainingSeats null 로 준다 — 빈 칸(제한 없음)으로
            capacity: schedule === 'OPEN' && typeof data.remainingSeats !== 'number'
              ? undefined
              : data.maxParticipants ?? data.capacity ?? GENERAL_DEFAULT_CAPACITY,
            // 썸네일은 1장(thumbnailUrl)만 쓴다 — 기존 모바일 썸네일은 무시
            thumbnailUrl: data.thumbnailUrl || '',
          });

          // 세미나 유형 초기화 (getDetail 응답의 seminarType 반영)
          setSeminarType(data.seminarType === 'CAREER' ? 'CAREER' : 'GENERAL');
          setOriginalSeminarType(data.seminarType === 'CAREER' ? 'CAREER' : 'GENERAL');
          // 승인 참여자 + 승인대기(결제 후 대기 포함) 어느 쪽이든 있으면 커리어→일반 변경 차단 대상
          // 서버 hasPaidParticipant(결제자 있음)가 오면 그 값을 먼저, 없으면 승인 참여자 + 승인대기 수로 판단
          setHasActiveParticipants(typeof data.hasPaidParticipant === 'boolean'
            ? data.hasPaidParticipant
            : ((data.currentParticipants ?? 0) + (data.pendingParticipantCount ?? 0)) > 0);

          // 기존 선택된 역들을 칩으로 보여 주도록 이름과 함께 보관
          setStationObjects(mappedNearbyStations);

          // 쿠폰 기준은 폼과 같은 함수로 — 불러온 값 그대로(화면 값과 비교한다)
          setSavedCouponBasis(couponBasisOf({
            seminarType: data.seminarType,
            format: data.format,
            schedule,
            price: Number(data.participationFee ?? data.price ?? 0),
            recruitmentEndDateTime: toFormDateTime(data.recruitmentEndDateTime),
          }));
          setSeminarStatus({
            isRecruitmentClosed: data.isRecruitmentClosed,
            isCancelled: data.isCancelled,
            startDateTime: data.startDateTime,
            endDateTime: data.endDateTime,
            recruitmentEndDateTime: data.recruitmentEndDateTime,
            seminarType: data.seminarType,
            format: data.format,
            schedule: data.schedule,
            salesClosed: data.salesClosed === true,
          });
        } else {
          console.error('세미나 조회 실패:', response.message);
        }
      } catch (error) {
        console.error('세미나 데이터 로드 실패:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchSeminarData();
  }, [isEditMode, id, reset]);

  // ─── 단계 이동 ────────────────────────────────────────────────────────

  /** 폰: 단계를 바꾸면 폼 맨 위(진행 막대 아래)로 */
  const scrollToFormTop = useCallback(() => {
    const top: number = (pageRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY - headerHeight;
    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  }, [headerHeight]);

  const canSelectStep = useCallback((target: SeminarFormStep): boolean => isEditMode || target <= maxStep, [isEditMode, maxStep]);

  const goToStep = useCallback((target: SeminarFormStep) => {
    setStep(target);
    setMaxStep((prev) => (target > prev ? target : prev));
    if (isPhone) {
      window.setTimeout(scrollToFormTop, 0);
    } else {
      // PC: 연 카드 머리가 앱 헤더(sticky) 뒤로 숨지 않게 헤더 높이만큼 띄워서
      window.setTimeout(() => {
        const card: HTMLElement | null = document.getElementById(`seminar-step-${target}`);
        if (!card) return;
        const top: number = card.getBoundingClientRect().top + window.scrollY - headerHeight - 20;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }, 30);
    }
  }, [isPhone, scrollToFormTop, headerHeight]);

  /** 그 칸이 있는 단계로 옮긴 뒤 칸에 포커스(단계가 숨어 있으면 포커스가 안 되므로 그린 다음에) */
  const goToField = useCallback((field: string) => {
    const target: SeminarFormStep | undefined = field === 'seminar-direct-deal-agree'
      ? LAST_STEP
      : FIELD_STEP[field as keyof typeof FIELD_STEP];
    if (target && target !== step) {
      setStep(target);
      setMaxStep((prev) => (target > prev ? target : prev));
      window.setTimeout(() => {
        if (!focusField(field)) {
          const card: HTMLElement | null = document.getElementById(`seminar-step-${target}`);
          // 고정 머리줄·단계 막대 아래로(scrollIntoView(start) 는 sticky 줄 뒤에 제목을 숨긴다)
          if (card) scrollBelowStickyBars(card);
        }
      }, 60);
      return;
    }
    if (!focusField(field) && target) {
      const card: HTMLElement | null = document.getElementById(`seminar-step-${target}`);
      if (card) scrollBelowStickyBars(card, { behavior: 'smooth' });
    }
  }, [step]);

  // ─── 저장 전 추가 규칙(커리어 유료·쿠폰 초안) — [다음]과 등록이 같이 쓴다 ─────────────

  /** 명치사 커리어는 유료 상품 — 참가비 0원(무료) 불가. (무료 세미나는 일반 유형) */
  const careerPriceProblem = (data: Partial<SeminarFormValues>): boolean => career && (Number(data.price) || 0) <= 0;

  /** 함께 만들 쿠폰 초안(새 등록·수정 화면에서 담아 둔 것)이 지금 참가비·모집 마감으로도 발급될 수 있는지 — 저장 뒤 실패하지 않게 먼저 막는다 */
  const couponProblemOf = (data: Partial<SeminarFormValues>): string | null => {
    if (!career || (couponDrafts.length === 0 && couponUpdates.length === 0)) return null;
    const basis: CouponBasis = couponBasisOf({ ...data, seminarType: effectiveType });
    const draftProblem: string | null = firstDraftProblem(couponDrafts, basis.listPrice, basis.recruitmentEnd);
    if (draftProblem) return draftProblem;
    // 수정 화면에서 담아 둔 고치기도 같은 기준(끝나는 날을 바꾼 것만 모집 마감 검사)
    for (const pending of couponUpdates) {
      const problem: string | null = pendingUpdateProblem(pending, basis.listPrice, basis.recruitmentEnd);
      if (problem) return `'${pending.payload.name}' 쿠폰: ${problem}`;
    }
    return null;
  };

  /** 수정 화면: 세미나를 저장한 뒤 담아 둔 쿠폰(새로 만들기·고치기)을 순서대로 반영한다. 못 한 것은 남겨 두고 사유를 돌려준다 */
  const applyPendingCoupons = async (): Promise<string[]> => {
    if (!id) return [];
    const failures: string[] = [];
    const keptDrafts: CouponDraft[] = [];
    const keptUpdates: CouponPendingUpdate[] = [];
    for (const draft of couponDrafts) {
      try {
        const res = await api.coupon.instructorCreate(id, draft.payload);
        if (res?.code !== 0) {
          failures.push(`· ${draft.payload.name}: ${res?.message || '쿠폰을 만들지 못했어요.'}`);
          keptDrafts.push(draft);
        }
      } catch (e: unknown) {
        failures.push(`· ${draft.payload.name}: ${errorMessageOf(e, '쿠폰을 만들지 못했어요.')}`);
        keptDrafts.push(draft);
      }
    }
    for (const pending of couponUpdates) {
      try {
        const res = await api.coupon.instructorUpdate(pending.id, pending.update);
        if (res?.code !== 0) {
          failures.push(`· ${pending.payload.name}: ${res?.message || '쿠폰을 수정하지 못했어요.'}`);
          keptUpdates.push(pending);
        }
      } catch (e: unknown) {
        failures.push(`· ${pending.payload.name}: ${errorMessageOf(e, '쿠폰을 수정하지 못했어요.')}`);
        keptUpdates.push(pending);
      }
    }
    setCouponDrafts(keptDrafts);
    setCouponUpdates(keptUpdates);
    return failures;
  };

  const alertCareerPrice = () => {
    goToField('price');
    showAlert('명치사 커리어 세미나는 참가비를 입력해야 해요.\n무료 세미나는 일반 유형으로 등록해 주세요.', () => { focusFieldAfterModal('price'); });
  };

  const alertCouponProblem = (problem: string) => {
    goToField('coupons');
    showAlert(`${problem}\n쿠폰을 수정한 뒤 다시 ${isEditMode ? '저장' : '등록'}해 주세요.`, () => { focusFieldAfterModal('coupons'); });
  };

  /** [다음] — 이 단계 칸만 검사하고, 틀리면 첫 오류 칸으로 스크롤·포커스 */
  const handleNext = async () => {
    if (step >= LAST_STEP) return;
    const fields: Array<keyof SeminarFormValues> = step <= 4 ? fieldsOfStep(step) : [];
    const ok: boolean = fields.length === 0 ? true : await trigger(fields);
    if (!ok) {
      const stepErrors = collectErrors({ ...watch(), seminarType: effectiveType });
      const first: keyof SeminarFormValues | undefined = fields.find((name) => !!stepErrors[name]) ?? fields[0];
      if (first) window.setTimeout(() => focusField(first), 30);
      return;
    }
    if (step === 3) {
      const current: SeminarFormValues = watch();
      if (careerPriceProblem(current)) {
        alertCareerPrice();
        return;
      }
      const problem: string | null = couponProblemOf(current);
      if (problem) {
        alertCouponProblem(problem);
        return;
      }
    }
    goToStep((step + 1) as SeminarFormStep);
  };

  const handlePrev = () => {
    if (step > 1) goToStep((step - 1) as SeminarFormStep);
  };

  // ─── 등록·저장 ────────────────────────────────────────────────────────

  // 검증 통과 후: 신규 등록이면 안내 확인 모달, 수정이면 바로 저장
  const onValidSubmit = (data: SeminarFormValues) => {
    // 커리어로 새로 등록·전환할 때는 외부 결제 유도 금지 동의가 있어야 한다
    if (career && originalSeminarType !== 'CAREER' && !directDealAgreed) {
      goToField('seminar-direct-deal-agree');
      showAlert('명치사 커리어 세미나 운영 원칙에 동의해 주세요.', () => { focusFieldAfterModal('seminar-direct-deal-agree'); });
      return;
    }
    if (careerPriceProblem(data)) {
      alertCareerPrice();
      return;
    }
    const problem: string | null = couponProblemOf(data);
    if (problem) {
      alertCouponProblem(problem);
      return;
    }
    if (!isEditMode) {
      setPendingSubmit(data);
      return;
    }
    onSubmit(data);
  };

  const onSubmit = async (data: SeminarFormValues) => {
    setPendingSubmit(null);
    setIsSubmitting(true);

    // 요청 전 로컬스토리지에 저장 (신규 등록 시에만)
    if (!isEditMode) {
      saveDraft({ formValues: data, couponDrafts, seminarType: effectiveType, stations: stationObjects, step, maxStep });
    }

    try {
      const apiPayload = buildPayload({ ...data, seminarType: effectiveType });
      // 신규 커리어 유료 등록이면 쿠폰 초안을 함께 보낸다(수정 화면은 즉시 생성 API 를 쓴다)
      const sendCoupons: boolean = !isEditMode && apiPayload.seminarType === 'CAREER' && apiPayload.participationFee > 0 && couponDrafts.length > 0;
      if (sendCoupons) apiPayload.coupons = couponDrafts.map((d: CouponDraft) => d.payload);

      const response = isEditMode
        ? await api.seminar.update(id!, apiPayload)
        : await api.seminar.create(apiPayload);

      if (response.code === 0) {
        if (!isEditMode) {
          submittedRef.current = true;
          clearDraft();
          setCouponDrafts([]);
        }
        const successMessage = isEditMode ? '세미나를 수정했어요.' : '세미나를 등록했어요.';
        const navigateTo = isEditMode ? `/seminars/${id}` : '/seminars';
        // 세미나는 저장됐지만 일부 쿠폰을 못 만들었으면 사유와 함께 알리고, 수정 화면에서 다시 만들 수 있게 한다
        const results: SeminarCouponCreateResult[] = Array.isArray(response.result?.couponResults) ? response.result.couponResults : [];
        const failed: SeminarCouponCreateResult[] = results.filter((r: SeminarCouponCreateResult) => !!r.error);
        const newId: string | undefined = response.result?.id ? String(response.result.id) : undefined;
        // 수정 화면: 담아 둔 쿠폰을 이제(저장된 값 기준으로) 반영 — 못 한 것은 수정 화면에 남기고 사유를 알린다
        const pendingFailures: string[] = isEditMode ? await applyPendingCoupons() : [];
        if (isEditMode && pendingFailures.length > 0) {
          setSavedCouponBasis(couponBasisOf({ ...data, seminarType: effectiveType }));
          setCouponReloadKey((key: number) => key + 1);
          reset(data);
          goToField('coupons');
          showAlert(
            `세미나는 저장됐고 쿠폰 ${pendingFailures.length}개는 반영하지 못했어요.\n쿠폰을 수정한 뒤 다시 저장해 주세요.\n\n${pendingFailures.join('\n')}`,
            () => { focusFieldAfterModal('coupons'); },
          );
        } else if (sendCoupons && failed.length > 0) {
          const reasons: string = failed
            .map((r: SeminarCouponCreateResult) => `· ${r.name || couponDrafts[r.index]?.payload.name || '쿠폰'}: ${r.error}`)
            .join('\n');
          showAlert(
            `세미나는 저장됐고 쿠폰 ${failed.length}개는 만들지 못했어요.\n수정 화면에서 다시 만들 수 있어요.\n\n${reasons}`,
            () => navigate(navigateTo),
            newId ? { label: '수정 화면으로', onClick: () => navigate(`/seminars/${newId}/edit`) } : undefined,
          );
        } else {
          showAlert(successMessage, () => navigate(navigateTo));
        }
      } else {
        throw new Error(response.message || `세미나 ${isEditMode ? '수정' : '등록'}에 실패했습니다.`);
      }
    } catch (error: any) {
      console.error(`세미나 ${isEditMode ? '수정' : '등록'} 실패:`, error);
      // 백엔드 에러 메시지를 그대로 노출 (409 등 구체 사유). 없으면 일반 문구 폴백.
      const backendMessage: string =
        error?.response?.data?.message ||
        (error instanceof Error ? error.message : '') ||
        `세미나 ${isEditMode ? '수정' : '등록'}에 실패했어요.\n다시 시도해 주세요.`;
      // 폐강이 필요한 잠금 에러면 참여인원 관리(폐강 위치)로 바로 이동하는 버튼 제공
      const needsCancel: boolean = isEditMode && !!id && backendMessage.includes('폐강');
      // 서버 검증(400)이 칸을 짚으면(field 또는 문구) 그 칸으로 — 얼럿을 닫으면 포커스
      const serverField: string | null = error?.response?.status === 400
        ? (error?.response?.data?.field as string | undefined)
          ?? error?.response?.data?.result?.field
          ?? fieldFromServerMessage(backendMessage, effectiveSchedule({ ...data, seminarType: effectiveType }))
        : null;
      if (serverField && !needsCancel) goToField(serverField);
      showAlert(
        backendMessage,
        serverField && !needsCancel ? () => { focusFieldAfterModal(serverField); } : undefined,
        needsCancel ? { label: '폐강하러 가기', onClick: () => navigate(`/seminars/${id}/participants`) } : undefined,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (formErrors: any) => {
    const keys = Object.keys(formErrors) as Array<keyof SeminarFormValues>;
    const firstErrorField: keyof SeminarFormValues | undefined =
      FIELD_ORDER.find((name) => keys.includes(name)) ?? keys[0];
    if (firstErrorField) {
      const errorMessage = formErrors[firstErrorField]?.message || '입력값을 확인해 주세요.';
      // 얼럿만 띄우고 끝내지 않는다 — 그 단계·칸으로 먼저 옮기고, 얼럿을 닫으면 칸에 포커스
      goToField(firstErrorField);
      showAlert(`${errorMessage}`, () => { focusFieldAfterModal(firstErrorField); });
    }
  };

  const submitForm = handleSubmit(onValidSubmit, onError);

  // 시작된 라이브·동영상: 접속 링크·안내만 저장 (링크가 바뀌면 서버가 참가자에게 알림)
  const handleSaveOnlineAccess = async () => {
    if (!id) return;
    const rawOnlineUrl: string = (watch('onlineUrl') || '').trim();
    const onlineGuide: string = (watch('onlineGuide') || '').trim();
    if (!isValidOnlineUrl(rawOnlineUrl)) {
      showAlert('링크 주소를 확인해 주세요.\n(예: zoom.us/j/123…)');
      return;
    }
    const onlineUrl: string = normalizeOnlineUrl(rawOnlineUrl);
    if (onlineGuide.length > ONLINE_GUIDE_MAX_LENGTH) {
      showAlert(`안내는 ${ONLINE_GUIDE_MAX_LENGTH}자까지 쓸 수 있어요.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await api.seminar.updateOnlineAccess(id, { onlineUrl, onlineGuide: onlineGuide || null });
      if (response.code !== 0) throw new Error(response.message);
      showAlert('접속 정보를 저장했어요.', () => navigate(`/seminars/${id}`));
    } catch (error: any) {
      showAlert(error?.response?.data?.message || '접속 정보를 저장하지 못했어요.\n다시 시도해 주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 세미나 관리 (수정 페이지) — 모집 마감 / 재모집 / 폐강
  const handleCloseRecruitment = async () => {
    if (!id) return;
    try {
      setManaging(true);
      await api.seminar.closeRecruitment(id);
      setSeminarStatus((prev) => (prev ? { ...prev, isRecruitmentClosed: true } : prev));
      showAlert('세미나 모집을 마감했어요.');
    } catch (error: any) {
      showAlert(error?.response?.data?.message || '모집 마감에 실패했어요.');
    } finally {
      setManaging(false);
    }
  };

  const handleReopenRecruitment = async () => {
    if (!id) return;
    try {
      setManaging(true);
      await api.seminar.reopenRecruitment(id);
      setSeminarStatus((prev) => (prev ? { ...prev, isRecruitmentClosed: false } : prev));
      showAlert('세미나 재모집을 시작했어요.');
    } catch (error: any) {
      showAlert(error?.response?.data?.message || '재모집에 실패했어요.');
    } finally {
      setManaging(false);
    }
  };

  const handleCancelSeminar = async () => {
    if (!id) return;
    const isCareerCancel = originalSeminarType === 'CAREER';
    // 커리어만 폐강 사유(참가자 통지용) 필수. 일반은 참가자가 없어 사유 없이 폐강.
    if (isCareerCancel && !cancelReason.trim()) return;
    const reason = isCareerCancel ? cancelReason.trim() : '세미나 폐강';
    try {
      setManaging(true);
      await api.seminar.cancelSeminar(id, reason);
      setCancelModalOpen(false);
      setCancelReason('');
      showAlert(
        isCareerCancel ? '세미나를 폐강했어요.\n결제한 참가자에게는 환불이 진행돼요.' : '세미나를 폐강했어요.',
        () => navigate(`/seminars/${id}`),
      );
    } catch (error: any) {
      setCancelModalOpen(false);
      showAlert(error?.response?.data?.message || '폐강 처리에 실패했어요.');
    } finally {
      setManaging(false);
    }
  };

  // 상시 동영상 판매 종료·재개 — 확인 후 PATCH. 이미 결제한 참가자는 계속 볼 수 있다
  const toggleSales = async (closing: boolean) => {
    if (!id) return;
    setTogglingSales(true);
    try {
      const response = await api.seminar.setSalesClosed(id, closing);
      if (response?.code !== 0) throw new Error(response?.message || '판매 상태를 바꾸지 못했어요.');
      setSeminarStatus((prev) => (prev ? { ...prev, salesClosed: closing } : prev));
      showAlert(closing ? '판매를 멈췄어요.\n이미 결제한 분은 계속 볼 수 있어요.' : '판매를 다시 시작했어요.');
    } catch (error: any) {
      showAlert(error?.response?.data?.message || error?.message || '판매 상태를 바꾸지 못했어요.\n잠시 후 다시 시도해 주세요.');
    } finally {
      setTogglingSales(false);
    }
  };

  const handleToggleSales = () => {
    const closing: boolean = !seminarStatus?.salesClosed;
    showAlert(
      closing
        ? '판매를 멈출까요?\n목록에서 빠지고 새로 결제할 수 없어요.\n이미 결제한 분은 계속 볼 수 있어요.'
        : '판매를 다시 시작할까요?\n목록에 다시 보이고 결제할 수 있어요.',
      undefined,
      { label: closing ? '판매 종료' : '판매 다시 시작', onClick: () => { void toggleSales(closing); } },
    );
  };

  // 관리 버튼 노출 조건 (수정 모드 + 상태 로드 후)
  const now = new Date();
  const notStarted = !seminarStatus?.startDateTime || new Date(seminarStatus.startDateTime) > now;
  const recruitmentPeriodValid = !seminarStatus?.recruitmentEndDateTime || new Date(seminarStatus.recruitmentEndDateTime) > now;
  const canManage = isEditMode && !!seminarStatus && !seminarStatus.isCancelled && notStarted;
  // 상시 동영상은 모집 마감·재모집 대신 판매 종료·재개를 쓴다
  const openSaleSaved: boolean = isEditMode && !!seminarStatus && !seminarStatus.isCancelled
    && seminarStatus.format === 'VOD' && seminarStatus.schedule === 'OPEN';
  const showClose = canManage && !seminarStatus?.isRecruitmentClosed && !openSaleSaved;
  const showReopen = canManage && seminarStatus?.isRecruitmentClosed === true && recruitmentPeriodValid && !openSaleSaved;
  // 폐강: 당일(및 일반)은 시작 전, 커리어 기간·회차·동영상은 종료 전까지 (서버 규칙과 같음)
  const cancelInProgress: boolean = !!seminarStatus && isSeminarInProgress(seminarStatus, now);
  // 폐강(취소·환불·통지)은 일반·커리어 모두 노출한다. 삭제는 폐강과 동일 동작이라 제거했다.
  const showCancel = isEditMode && !!seminarStatus && canCancelSeminar(seminarStatus, now);

  const format: SeminarFormatValue = watchValues.format ?? 'OFFLINE';
  const schedule: SeminarScheduleValue = effectiveSchedule({ ...watchValues, seminarType: effectiveType });
  const isOnlineFormat: boolean = career && format !== 'OFFLINE';
  const showAddress: boolean = needsAddress({ ...watchValues, seminarType: effectiveType });
  // 동영상 상시 판매 — 판매 시작일만, 모집 기간 없음, 정원 선택
  const openSale: boolean = career && schedule === 'OPEN';
  // 결제자(참여자)가 있는 커리어 세미나 — 참가비·일정·장소는 바꿀 수 없다(서버 규칙과 같음). 칸을 잠그고 한 줄 안내
  const lockPriceSchedule: boolean = isEditMode && originalSeminarType === 'CAREER' && hasActiveParticipants;

  const handleAddressChange = (addressData: KakaoAddressDocument) => {
    setValue('address', addressData.address_name, { shouldValidate: true });
    setValue('addressCode', addressData.address.b_code);
    setValue('latitude', addressData.y !== undefined ? String(addressData.y) : '');
    setValue('longitude', addressData.x !== undefined ? String(addressData.x) : '');
  };

  const handleStationsChange = (next: DraftStation[]) => {
    setStationObjects(next);
    setValue('stations', next.map((s) => s.id), { shouldValidate: true, shouldDirty: true });
  };

  // 목록 카드 일정 줄 미리보기 + 환불 기준일 안내
  const scheduleLabel: string | null = buildScheduleLabel({
    format: career ? format : 'OFFLINE',
    schedule,
    singleStart: watchValues.startDateTime,
    singleEnd: watchValues.endDateTime,
    periodStartDate: watchValues.periodStartDate,
    periodEndDate: watchValues.periodEndDate,
    sessions: watchValues.sessions,
  });
  const refundGuide: string = !career
    ? '하루 행사면 시작일만 고르면 돼요.'
    : openSale
      ? '판매 시작일부터 목록에 보이고 결제하면 바로 볼 수 있어요.\n판매를 멈추려면 수정 화면이나 상세에서 판매 종료를 눌러 주세요.'
    : format === 'VOD'
      ? '환불 기준일은 시청 시작일이에요. 시청이 시작되면 환불되지 않아요.'
      : schedule === 'SESSIONS'
        ? '환불 기준일은 1회차 시작이에요. 1회차가 시작되면 환불되지 않아요.'
        : '환불 기준일은 세미나 시작일이에요. 시작되면 환불되지 않아요.';
  // 여러 날에 걸쳐 진행되는 커리어 세미나는 진행 중 폐강 시 전액 환불 고지
  const showMidwayCancelNotice: boolean = career && (format === 'VOD' || schedule !== 'SINGLE');
  // 상시 동영상은 회차가 없다 — "판매 중 폐강하면 이미 결제한 분 전원" 문구. 기간·회차형은 "전 회차"
  const midwayNoticeText: string = openSale ? OPEN_CANCEL_NOTICE : '진행 중 폐강하면 전 회차 전액 환불되며 정산에서 차감됩니다.';
  // 동영상 환불 고지 — 참가자가 접속 링크를 열면 환불되지 않는다(상시는 결제 후 7일·접속 전 규칙)
  const vodRefundNotice: string | null = !career || format !== 'VOD'
    ? null
    : openSale
      ? '상시 동영상은 결제 후 7일 이내·접속 전에만 환불돼요.\n판매를 멈춰도 이미 결제한 분은 계속 볼 수 있어요.\n참가자가 접속 링크를 열면 환불되지 않아요.'
      : '참가자가 접속 링크를 열면 환불되지 않아요.';

  // ─── 단계별 빠진 항목(확인 단계·PC 오른쪽 칸·등록 버튼) ─────────────────────────

  const valuesKey: string = JSON.stringify(watchValues);
  const liveErrors = useMemo(
    () => collectErrors({ ...watchValues, seminarType: effectiveType }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valuesKey, effectiveType],
  );
  const stepProblems: Partial<Record<SeminarFormStep, StepProblem>> = {};
  const stepMissing: Partial<Record<SeminarFormStep, string[]>> = {};
  const addMissing = (fieldStep: SeminarFormStep, field: string, message: string): void => {
    if (!stepProblems[fieldStep]) stepProblems[fieldStep] = { field, message };
    stepMissing[fieldStep] = [...(stepMissing[fieldStep] ?? []), field];
  };
  FIELD_ORDER.forEach((field) => {
    const message: string | undefined = liveErrors[field];
    const fieldStep: SeminarFormStep | undefined = FIELD_STEP[field];
    if (message && fieldStep) addMissing(fieldStep, field, message);
  });
  if (!liveErrors.price && careerPriceProblem(watchValues)) {
    addMissing(3, 'price', '명치사 커리어는 참가비를 입력해야 해요.');
  }
  const liveCouponProblem: string | null = couponProblemOf(watchValues);
  if (liveCouponProblem) addMissing(3, 'coupons', liveCouponProblem);
  const needsDirectDealAgree: boolean = career && originalSeminarType !== 'CAREER';
  const readyToSubmit: boolean = Object.keys(stepProblems).length === 0 && !(needsDirectDealAgree && !directDealAgreed);
  const isStepDone = (target: SeminarFormStep): boolean =>
    target < LAST_STEP && !stepProblems[target] && (isEditMode || maxStep > target);

  // ─── 임시 저장(새 등록만) ─────────────────────────────────────────────────

  const userHasInput: boolean = hasUserInput(watchValues, couponDrafts);
  const stationsKey: string = stationObjects.map((s) => s.id).join(',');
  useEffect(() => {
    if (isEditMode || pendingDraft || submittedRef.current || !userHasInput) return undefined;
    const timer: number = window.setTimeout(() => {
      if (submittedRef.current) return;
      const savedAt: number | null = saveDraft({
        formValues: watch(),
        couponDrafts,
        seminarType,
        stations: stationObjects,
        step,
        maxStep,
      });
      if (savedAt) setDraftSavedAt(savedAt);
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesKey, couponDrafts, seminarType, stationsKey, step, maxStep, pendingDraft, isEditMode, userHasInput]);

  const handleResumeDraft = () => {
    const saved: SeminarDraft | null = pendingDraft;
    if (!saved) return;
    const values: Partial<SeminarFormValues> = saved.formValues ?? {};
    reset({
      ...EMPTY_VALUES,
      ...values,
      // 강사명은 지금 프로필 값을 쓴다
      instructor: watch('instructor') || values.instructor || '',
      originalStartDateTime: '',
      originalEndDateTime: '',
      originalTitle: '',
      contact: '',
      thumbnailUrl: values.thumbnailUrl || saved.pcImages?.[0] || '',
    });
    setCouponDrafts(saved.couponDrafts ?? []);
    setStationObjects(saved.stations ?? []);
    // 이어 쓰기 중에는 커리어 안내를 띄우지 않는다 — 지금 커리어를 못 여는 강사면 조용히 일반으로
    careerGateAutoDoneRef.current = true;
    setSeminarType(saved.seminarType === 'CAREER' && (!profileChecked || careerEligible) ? 'CAREER' : 'GENERAL');
    const savedStep: SeminarFormStep = (saved.step && saved.step >= 1 && saved.step <= 5 ? saved.step : 1) as SeminarFormStep;
    const savedMax: SeminarFormStep = (saved.maxStep && saved.maxStep >= savedStep && saved.maxStep <= 5 ? saved.maxStep : savedStep) as SeminarFormStep;
    setStep(savedStep);
    setMaxStep(savedMax);
    setEditorKey((key) => key + 1);
    setDraftSavedAt(saved.savedAt);
    setPendingDraft(null);
  };

  const handleFreshStart = () => {
    clearDraft();
    setPendingDraft(null);
  };

  // ─── 나가기 ──────────────────────────────────────────────────────────

  const leave = () => {
    setExitConfirmOpen(false);
    if (location.key !== 'default') navigate(-1);
    else navigate(isEditMode && id ? `/seminars/${id}` : '/seminars');
  };

  const handleExit = () => {
    const changed: boolean = isEditMode ? isDirty || couponDrafts.length > 0 || couponUpdates.length > 0 : userHasInput;
    if (changed) setExitConfirmOpen(true);
    else leave();
  };

  // 세미나 폐강 사유 모달 (수정 페이지·접속 정보 수정 화면 공용). 문구는 문장마다 줄바꿈.
  const cancelModal = (
    <CustomModal
      title='세미나 폐강'
      width={isMobile ? '90%' : '460px'}
      onClose={() => setCancelModalOpen(false)}
      open={cancelModalOpen}
      height='auto'
    >
      <Container width={"100%"} display='block'>
        {originalSeminarType === 'CAREER' ? (
          <>
            <Text size='15px' textcolor='#1f2937' margin='0 0 12px 0' style={{ lineHeight: 1.7, whiteSpace: 'pre-line', wordBreak: 'keep-all' }}>
              {'결제한 참가자에게는 폐강 시 자동으로 전액 환불돼요.'}
              {cancelInProgress && seminarStatus ? `\n${midwayCancelNoticeOf(seminarStatus)}` : ''}
              {'\n해당 세미나를 폐강할까요?'}
            </Text>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="참가자에게 전달될 폐강 사유를 입력하세요..."
              maxLength={500}
              aria-label="폐강 사유"
              style={{
                width: '100%', minHeight: '110px', padding: '12px',
                border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.95rem',
                fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <Text size='12px' textcolor='#9ca3af' margin='8px 0 0 0' style={{ lineHeight: 1.5 }}>
              · 참여자에게는 자동으로 메일이 발송됩니다.
            </Text>
          </>
        ) : (
          <>
            <Text size='15px' textcolor='#1f2937' margin='0 0 10px 0' style={{ lineHeight: 1.7 }}>
              해당 세미나를 폐강할까요?
            </Text>
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '10px 14px' }}>
              <Text size='14px' textcolor='#dc2626' margin='0' style={{ lineHeight: 1.6, fontWeight: 700 }}>
                ⚠️ 폐강 후에는 되돌릴 수 없습니다.
              </Text>
            </div>
          </>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Button onClick={() => setCancelModalOpen(false)} justifycontents='center'
            color='#666' bgcolor='#e5e7eb' style={{ flex: 1 }}>
            닫기
          </Button>
          <Button onClick={handleCancelSeminar} justifycontents='center'
            color='#fff' bgcolor='#dc2626'
            disabled={managing || (originalSeminarType === 'CAREER' && !cancelReason.trim())} style={{ flex: 1 }}>
            {managing ? <ButtonSpinner /> : '폐강하기'}
          </Button>
        </div>
      </Container>
    </CustomModal>
  );

  // 공용 알림 모달 — 문구는 문장마다 줄바꿈(\n)
  const alertModal = (
    <CustomModal
      title='알림'
      width={isMobile ? '90%' : '400px'}
      onClose={closeAlert}
      open={showAlertModal}
      height='auto'
    >
      <Container width={"100%"} display='block'>
        {/* 얼럿 문구는 문장마다 줄바꿈(\n) — 그대로 보여 준다 */}
        <Text textalign='center' size='16px' margin='0 0 20px 0' style={{ lineHeight: 1.6, whiteSpace: 'pre-line', wordBreak: 'keep-all' }}>
          {alertMessage}
        </Text>
        {alertAction ? (
          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              onClick={closeAlert}
              justifycontents='center'
              color='#666'
              bgcolor='#e5e7eb'
              style={{ flex: 1 }}
            >
              닫기
            </Button>
            <Button
              // 보조 액션은 닫기 콜백(목록 이동 등) 대신 자기 동작만 한다
              onClick={() => { const act = alertAction; setShowAlertModal(false); setAlertCallback(null); act.onClick(); }}
              justifycontents='center'
              color='#fff'
              bgcolor={designSystem.colors.primary}
              style={{ flex: 1 }}
            >
              {alertAction.label}
            </Button>
          </div>
        ) : (
          <Button
            onClick={closeAlert}
            justifycontents='center'
            color='#fff'
            bgcolor={designSystem.colors.primary}
          >
            확인
          </Button>
        )}
      </Container>
    </CustomModal>
  );

  // 접근 권한 없음 → 폼/데이터 렌더 차단, 표준 안내 모달만 노출하고 상세로 이동
  if (accessDeniedMessage !== null) {
    return (
      <Container>
        <ResultModal
          open={true}
          status={false}
          title="접근 권한 없음"
          description={accessDeniedMessage}
          buttontext={deniedGoParticipants ? '참여인원 관리로' : '확인'}
          onClose={() => navigate(`/seminars/${id}`)}
          onClick={() => navigate(deniedGoParticipants ? `/seminars/${id}/participants` : `/seminars/${id}`)}
        />
      </Container>
    );
  }

  if (isLoadingData) {
    return (
      <>
        <LayoutWrapper>
          <Header>
            <Title>세미나 수정</Title>
          </Header>
          <FormContent>
            <LoadingSpinner placement="page" minHeight="300px" />
          </FormContent>
        </LayoutWrapper>
      </>
    );
  }

  // 시작된 커리어 라이브·동영상 — 접속 링크·안내만 바꿀 수 있다
  if (accessOnlyMode && (format === 'LIVE' || format === 'VOD')) {
    return (
      <>
        <LayoutWrapper>
          <Header>
            <Title>접속 정보 수정</Title>
          </Header>
          <FormContent>
            <GuideLines>
              {'이미 시작된 세미나는 접속 링크와 안내만 바꿀 수 있어요.\n일정·장소·참가비를 바꾸려면 아래에서 폐강한 뒤 다시 등록해 주세요.'}
            </GuideLines>
            <SectionTitle id="seminar-location">접속</SectionTitle>
            <OnlineAccessFields
              format={format}
              url={watchValues.onlineUrl || ''}
              guide={watchValues.onlineGuide || ''}
              onUrlChange={(value) => setValue('onlineUrl', value, { shouldValidate: true })}
              onGuideChange={(value) => setValue('onlineGuide', value, { shouldValidate: true })}
              urlError={errors.onlineUrl?.message}
              guideError={errors.onlineGuide?.message}
              openSale={format === 'VOD' && (openSaleSaved || schedule === 'OPEN')}
            />
            {(showCancel || openSaleSaved) && (
              <ButtonGroup>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginRight: 'auto' }}>
                  {openSaleSaved && (
                    <Button onClick={handleToggleSales} disabled={togglingSales} justifycontents="center"
                      bgcolor="#fff" color={designSystem.colors.primary} width="auto"
                      style={{ border: `1px solid ${designSystem.colors.primary}` }}>
                      {togglingSales ? <ButtonSpinner /> : seminarStatus?.salesClosed ? '판매 다시 시작' : '판매 종료'}
                    </Button>
                  )}
                  {showCancel && (
                    <Button onClick={() => setCancelModalOpen(true)} disabled={managing} justifycontents="center"
                      bgcolor="#fff" color="#dc2626" width="auto" style={{ border: '1px solid #dc2626' }}>
                      폐강하기
                    </Button>
                  )}
                </div>
              </ButtonGroup>
            )}
            {/* 상시 동영상은 판매 중에도 쿠폰을 만들고·중지하고·고칠 수 있다(모집 마감이 없어 끝나는 날은 직접 정한다) */}
            {openSaleSaved && id && (Number(watchValues.price) || 0) > 0 && (
              <div data-field="coupons">
                <InstructorCouponSection seminarId={id} listPrice={Number(watchValues.price) || 0} recruitmentEnd={null} />
              </div>
            )}
            {openSaleSaved && (
              <GuideLines>
                {seminarStatus?.salesClosed
                  ? '지금은 판매를 멈춘 상태예요.\n이미 결제한 분은 계속 볼 수 있어요.'
                  : '판매를 멈춰도 이미 결제한 분은 계속 볼 수 있어요.'}
              </GuideLines>
            )}
          </FormContent>
        </LayoutWrapper>
        <FormSaveBar>
          <SubmitButton type="button" onClick={handleSaveOnlineAccess} disabled={isSubmitting}>
            {isSubmitting ? <ButtonSpinner /> : '접속 정보 저장'}
          </SubmitButton>
        </FormSaveBar>
        {cancelModal}
        {alertModal}
      </>
    );
  }

  const contextValue: SeminarFormContextValue = {
    control,
    errors,
    values: watchValues,
    setValue,
    watch,
    isEditMode,
    seminarId: id,
    paymentEnabled,
    seminarType,
    originalSeminarType,
    career,
    onTypeChange: handleSeminarTypeChange,
    categoryGroups,
    format,
    schedule,
    openSale,
    isOnlineFormat,
    showAddress,
    lockPriceSchedule,
    today,
    onFormatChange: handleFormatChange,
    onScheduleChange: handleScheduleChange,
    onAddressChange: handleAddressChange,
    stationObjects,
    onStationsChange: handleStationsChange,
    refundGuide,
    showMidwayCancelNotice,
    midwayNoticeText,
    vodRefundNotice,
    scheduleLabel,
    openSaleSaved,
    couponDrafts,
    onCouponDraftsChange: (next: CouponDraft[]) => {
      setCouponDrafts(next);
      if (next.length > 0) setCouponDraftNotice('');
    },
    couponDraftNotice,
    couponBasis,
    couponDeferred: isEditMode && !!savedCouponBasis && !sameCouponBasis(couponBasis, savedCouponBasis),
    couponUpdates,
    onCouponUpdatesChange: setCouponUpdates,
    couponReloadKey,
    editorKey,
    showAlert,
    directDealAgreed,
    setDirectDealAgreed,
    stepProblems,
    stepMissing,
    currentStep: step,
    readyToSubmit,
    needsDirectDealAgree,
    liveErrors,
    goToField,
    goToStep,
    manage: {
      status: seminarStatus,
      showClose,
      showReopen,
      showCancel,
      managing,
      togglingSales,
      onClose: handleCloseRecruitment,
      onReopen: handleReopenRecruitment,
      onCancel: () => setCancelModalOpen(true),
      onToggleSales: handleToggleSales,
    },
  };

  const submitLabel: string = isEditMode ? '저장하기' : '세미나 등록하기';
  const savedText: string = isEditMode || !draftSavedAt
    ? ''
    : isPhone
      ? '임시 저장됨'
      : `임시 저장됨 · ${String(new Date(draftSavedAt).getHours()).padStart(2, '0')}:${String(new Date(draftSavedAt).getMinutes()).padStart(2, '0')}`;

  const stepBodies: Record<SeminarFormStep, React.ReactNode> = {
    1: <StepBasic />,
    2: <StepSchedulePlace />,
    3: <StepRecruitPrice />,
    4: <StepIntro />,
    5: <StepConfirm showPreview={isPhone} showChecklist={isPhone} />,
  };

  const stepCards = STEP_META.map((meta) => (
    <StepCard
      key={meta.step}
      step={meta.step}
      active={step === meta.step}
      isPhone={isPhone}
      done={isStepDone(meta.step)}
      locked={!canSelectStep(meta.step)}
      sub={stepSubOf(meta.step, career, paymentEnabled, isPhone)}
      titleOverride={isEditMode && meta.step === LAST_STEP ? '확인하고 저장' : undefined}
      onOpen={() => goToStep(meta.step)}
      footer={meta.step < LAST_STEP ? (
        <PrimaryButton type="button" onClick={handleNext} style={{ flex: '0 0 auto', minWidth: 160, height: 48 }}>
          다음 단계
        </PrimaryButton>
      ) : undefined}
    >
      {stepBodies[meta.step]}
    </StepCard>
  ));

  const resumeBanner = pendingDraft ? (
    <ResumeDraftBanner
      title={pendingDraft.formValues?.title?.trim() ?? ''}
      savedAt={pendingDraft.savedAt}
      onResume={handleResumeDraft}
      onFresh={handleFreshStart}
    />
  ) : null;

  return (
    <SeminarFormContext.Provider value={contextValue}>
      {/* 세미나 관리(마감/재모집/폐강) 처리 중 전체 로딩 오버레이 */}
      {managing && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(255, 255, 255, 0.72)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <LoadingSpinner minHeight="auto" />
        </div>
      )}

      <FormPage ref={pageRef}>
        {isPhone ? (
          <FormTopBar title={isEditMode ? '세미나 수정' : '세미나 등록'} savedText={savedText} onExit={handleExit} />
        ) : (
          <PcFormHeader
            title={isEditMode ? '세미나 수정' : '세미나 등록'}
            savedText={savedText}
            onExit={handleExit}
          />
        )}

        {isPhone ? (
          <>
            <ProgressStrip step={step} top={headerHeight} editMode={isEditMode} canSelect={canSelectStep} onSelect={goToStep} />
            {resumeBanner}
            <form onSubmit={(e) => { e.preventDefault(); }} onKeyDown={preventEnterSubmit} noValidate>
              {stepCards}
            </form>
            {/* 아래 고정 줄 — 페이지 틀의 마지막 자식(sticky) */}
            <PhoneActionBar>
              {step > 1 && <GhostButton type="button" onClick={handlePrev}>이전</GhostButton>}
              {step < LAST_STEP ? (
                <PrimaryButton type="button" onClick={handleNext}>다음</PrimaryButton>
              ) : (
                <PrimaryButton
                  type="button"
                  look={readyToSubmit ? 'ready' : 'waiting'}
                  onClick={submitForm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <ButtonSpinner /> : submitLabel}
                </PrimaryButton>
              )}
            </PhoneActionBar>
          </>
        ) : (
          <PcGrid>
            <main style={{ minWidth: 0 }}>
              {resumeBanner}
              <form onSubmit={(e) => { e.preventDefault(); }} onKeyDown={preventEnterSubmit} noValidate>
                {stepCards}
              </form>
            </main>
            <PcSide top={headerHeight}>
              <SeminarFormPreview width="100%" />
              <StepStatusList isDone={isStepDone} />
              <PrimaryButton
                type="button"
                look={readyToSubmit ? 'ready' : 'waiting'}
                onClick={submitForm}
                disabled={isSubmitting}
                style={{ flex: '0 0 auto' }}
              >
                {isSubmitting ? <ButtonSpinner /> : submitLabel}
              </PrimaryButton>
            </PcSide>
          </PcGrid>
        )}
      </FormPage>

      {cancelModal}

      {alertModal}

      <CareerGateSheet
        open={careerGateOpen}
        hasAccount={hasSettlementAccount}
        hasTerms={hasInstructorTerms}
        editMode={isEditMode}
        onGoEdit={goInstructorEdit}
        onClose={closeCareerGate}
      />

      {/* 나가기 확인 — 입력이 있을 때만(열릴 때만 그린다) */}
      {exitConfirmOpen && (
        <CustomModal title="나가기" width={isMobile ? '90%' : '400px'} onClose={() => setExitConfirmOpen(false)} open height="auto">
          <Container width={"100%"} display='block'>
            <Text textalign='center' size='15px' margin='0 0 20px 0' style={{ lineHeight: 1.7, whiteSpace: 'pre-line', wordBreak: 'keep-all' }}>
              {isEditMode
                ? '수정한 내용이 저장되지 않아요.\n나갈까요?'
                : '쓰던 내용은 임시 저장돼 있어요.\n다음에 이어서 쓸 수 있어요.\n나갈까요?'}
            </Text>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button onClick={() => setExitConfirmOpen(false)} justifycontents='center' color='#666' bgcolor='#e5e7eb' style={{ flex: 1 }}>
                계속 쓰기
              </Button>
              <Button onClick={leave} justifycontents='center' color='#fff' bgcolor={designSystem.colors.primary} style={{ flex: 1 }}>
                나가기
              </Button>
            </div>
          </Container>
        </CustomModal>
      )}

      {/* 신규 등록 전 안내 확인 — 유형에 따라 문구 분기.
          커리어 선택 시: 참여자 잠금 안내(+ 여러 날 진행이면 진행 중 폐강 환불 고지). 결제기능 ON에서 일반 선택 시: 커리어 전환 유도. 결제기능 OFF: 기존 안내. */}
      <CustomModal
        title={career ? '세미나 등록 안내' : (paymentEnabled ? '일반 세미나로 등록' : '세미나 등록')}
        width={isMobile ? '90%' : '420px'}
        onClose={() => setPendingSubmit(null)}
        open={!!pendingSubmit}
        height='auto'
      >
        <Container width={"100%"} display='block'>
          <Text textalign='left' size='15px' margin='0 0 20px 0' style={{ lineHeight: 1.7, whiteSpace: 'pre-line', wordBreak: 'keep-all' }}>
            {career
              ? `참여자(결제자)가 생기면 일정·장소·참가비는 바꿀 수 없어요.\n바꾸려면 폐강 후 다시 등록해야 해요.${showMidwayCancelNotice ? `\n${midwayNoticeText}` : ''}${couponDrafts.length > 0 ? `\n만들어 둔 쿠폰 ${couponDrafts.length}개도 함께 발급돼요.` : ''}\n\n등록할까요?`
              : paymentEnabled
                ? '명치사 커리어로 등록하면 참가 신청·결제, 참여자 관리·정산, 별점·후기, 참여자 알림을 쓸 수 있어요.\n일반은 안내용 카드만 보여요.\n\n일반으로 등록할까요?'
                : '세미나를 등록할까요?'}
          </Text>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              onClick={() => setPendingSubmit(null)}
              justifycontents='center'
              color='#666'
              bgcolor='#e5e7eb'
              style={{ flex: 1 }}
            >
              취소
            </Button>
            <Button
              onClick={() => { if (pendingSubmit) onSubmit(pendingSubmit); }}
              justifycontents='center'
              color='#fff'
              bgcolor={designSystem.colors.primary}
              style={{ flex: 1 }}
            >
              등록
            </Button>
          </div>
        </Container>
      </CustomModal>
    </SeminarFormContext.Provider>
  );
};

/**
 * 등록(/seminars/new) → "수정 화면으로"(/seminars/:id/edit)는 같은 컴포넌트라 React 가 인스턴스를 재사용한다.
 * 초안·유형·로딩 상태가 남지 않게 경로의 id 마다 새로 만든다.
 */
const SeminarFormRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <SeminarForm key={id ?? 'new'} />;
};

export default SeminarFormRoute;
